/**
 * Hooks pour les villes météo mémorisées (favoris).
 *
 * Les favoris sont dérivés de board.settings — on invalide la query board
 * après chaque ajout/suppression pour que le cache reste cohérent.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  addWeatherFavorite,
  removeWeatherFavorite,
  getWeatherFavorites,
  createWeatherCard,
  queryKeys,
  type WeatherFavorite,
} from "@/lib/api";

export type { WeatherFavorite };

// ─── Lecture ──────────────────────────────────────────────────────

export function useWeatherFavorites(slug: string) {
  return useQuery({
    queryKey: [...queryKeys.board(slug), "weather-favorites"],
    queryFn: async () => {
      const result = await getWeatherFavorites(slug);
      if (!result.ok) throw new Error(result.error);
      return result.data.favorites;
    },
    enabled: !!slug,
    // Les favoris changent peu — cache long pour éviter les requêtes inutiles.
    staleTime: 60_000,
  });
}

// ─── Ajout ────────────────────────────────────────────────────────

export function useAddWeatherFavorite(slug: string) {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (favorite: WeatherFavorite) => addWeatherFavorite(slug, favorite),
    onSuccess: (result) => {
      if (!result.ok) { toast.error(result.error); return; }
      qc.invalidateQueries({ queryKey: [...queryKeys.board(slug), "weather-favorites"] });
      toast.success(t("board.weather_favorite_saved"));
    },
    onError: () => toast.error(t("board.weather_favorite_error")),
  });
}

// ─── Suppression ──────────────────────────────────────────────────

export function useRemoveWeatherFavorite(slug: string) {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (favoriteId: string) => removeWeatherFavorite(slug, favoriteId),
    onSuccess: (result) => {
      if (!result.ok) { toast.error(result.error); return; }
      qc.invalidateQueries({ queryKey: [...queryKeys.board(slug), "weather-favorites"] });
      toast.success(t("board.weather_favorite_removed"));
    },
    onError: () => toast.error(t("board.weather_favorite_error")),
  });
}

// ─── Ajouter une carte depuis un favori ───────────────────────────

/**
 * Crée une carte météo à partir d'un favori (re-utilise createWeatherCard).
 * Invalide la liste des cartes après succès.
 */
export function useAddCardFromFavorite(slug: string) {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: (fav: WeatherFavorite) =>
      createWeatherCard(slug, {
        city_query: fav.city_query,
        city_label: fav.city_label,
        country_code: fav.country_code,
        latitude: fav.latitude,
        longitude: fav.longitude,
        timezone: fav.timezone,
        layout: fav.layout,
        style: fav.style,
      }),
    onSuccess: (result) => {
      if (!result.ok) { toast.error(result.error); return; }
      qc.invalidateQueries({ queryKey: queryKeys.weatherCards(slug) });
      qc.invalidateQueries({ queryKey: queryKeys.display(slug) });
      if (result.data.reused) {
        toast.info(t("board.weather_reused_toast"));
      } else {
        toast.success(t("board.weather_added_toast"));
      }
    },
    onError: () => toast.error(t("board.weather_error_toast")),
  });
}
